import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, UsersIcon, ShoppingCartIcon, FileTextIcon, PackageIcon, LogInIcon, ChevronDownIcon, ChevronRightIcon, BoxIcon, SettingsIcon, ClipboardCheckIcon } from 'lucide-react';
const navGroups = [{
  title: 'Dashboard',
  icon: LayoutDashboardIcon,
  links: [{
    label: 'Overview',
    path: '/dashboard'
  }]
}, {
  title: 'Supplier & Contract Management',
  icon: UsersIcon,
  links: [{
    label: 'Supplier Management',
    path: '/suppliers'
  }, {
    label: 'Supplier Approval',
    path: '/supplier-approval'
  }, {
    label: 'Supplier Performance',
    path: '/supplier-kpi'
  }, {
    label: 'Contract Management',
    path: '/contracts'
  }]
}, {
  title: 'Procurement Operations',
  icon: ShoppingCartIcon,
  links: [{
    label: 'Annual Plan',
    path: '/procurement-plan'
  }, {
    label: 'Annual Plan Approval',
    path: '/annual-plan-approval'
  }, {
    label: 'Purchase Requisition',
    path: '/purchase-requisitions'
  }, {
    label: 'PR Approval',
    path: '/pr-approval'
  }, {
    label: 'EOI',
    path: '/eoi'
  }, {
    label: 'RFP',
    path: '/rfp'
  }, {
    label: 'RFQ',
    path: '/rfq'
  }, {
    label: 'Negotiation',
    path: '/negotiation'
  }, {
    label: 'NOA',
    path: '/noa'
  }, {
    label: 'EOI Approval',
    path: '/eoi-approval'
  }, {
    label: 'RFP Approval',
    path: '/rfp-approval'
  }, {
    label: 'RFQ Approval',
    path: '/rfq-approval'
  }, {
    label: 'NOA Approval',
    path: '/noa-approval'
  }]
}, {
  title: 'Purchase Orders',
  icon: FileTextIcon,
  links: [{
    label: 'PO List',
    path: '/purchase-orders'
  }, {
    label: 'PO Approval',
    path: '/po-approval'
  }]
}, {
  title: 'Receiving, Inventory & Warehouse',
  icon: PackageIcon,
  links: [{
    label: 'Product Configuration',
    path: '/products'
  }, {
    label: 'Requisition',
    path: '/requisitions'
  }, {
    label: 'Requisition Approval',
    path: '/requisition-approval'
  }, {
    label: 'Receive',
    path: '/receives'
  }, {
    label: 'Quality Control',
    path: '/qc'
  }, {
    label: 'GRN Report',
    path: '/grn'
  }, {
    label: 'Disposal & Repair',
    path: '/disposal-repair'
  }, {
    label: 'Stock',
    path: '/stock'
  }, {
    label: 'Stock Issue',
    path: '/stock/issue'
  }, {
    label: 'Stock Thresholds',
    path: '/stock-thresholds'
  }, {
    label: 'Warehouses',
    path: '/master-settings/warehouses'
  }]
}, {
  title: 'Master Settings',
  icon: SettingsIcon,
  links: [{
    label: 'Departments',
    path: '/master-settings/departments'
  }, {
    label: 'Categories',
    path: '/master-settings/categories'
  }, {
    label: 'Units of Measure',
    path: '/master-settings/units-of-measure'
  }]
}, {
  title: 'Supplier Portal',
  icon: LogInIcon,
  links: [{
    label: 'Tender Board',
    path: '/tender-board'
  }, {
    label: 'Supplier Sign-Up',
    path: '/supplier-signup'
  }]
}];
export function Sidebar() {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Dashboard: true,
    'Supplier & Contract Management': location.pathname.includes('supplier') && !location.pathname.includes('supplier-signup') && !location.pathname.includes('tender-board') || location.pathname.includes('contract'),
    'Procurement Operations': location.pathname.includes('pr-') || location.pathname.includes('approval') || location.pathname.includes('purchase-requisitions') || location.pathname.includes('procurement-plan') || location.pathname.startsWith('/eoi') || location.pathname.startsWith('/rfp') || location.pathname.startsWith('/rfq') || location.pathname.startsWith('/negotiation') || location.pathname.startsWith('/noa'),
    'Purchase Orders': location.pathname.includes('po-') || location.pathname.includes('purchase-orders'),
    'Receiving, Inventory & Warehouse': location.pathname.includes('product') || location.pathname.includes('requisition') || location.pathname.includes('receive') || location.pathname.includes('qc') || location.pathname.includes('disposal') || location.pathname.includes('stock') || location.pathname.includes('master-settings/warehouses'),
    'Master Settings': location.pathname.includes('master-settings'),
    'Supplier Portal': location.pathname.includes('tender-board') || location.pathname.includes('supplier-signup')
  });
  const toggleGroup = (title: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };
  return <div className="w-64 bg-slate-800 text-white flex flex-col h-screen flex-shrink-0 shadow-xl z-20" data-id="element-486">
      <div className="h-16 flex items-center px-6 border-b border-slate-700 bg-slate-900" data-id="element-487">
        <BoxIcon className="h-8 w-8 text-indigo-400 mr-3" data-id="element-488" />
        <span className="text-xl font-bold tracking-tight" data-id="element-489">PLMS</span>
      </div>

      <div className="flex-1 overflow-y-auto py-4 custom-scrollbar" data-id="element-490">
        <nav className="px-3 space-y-1" data-id="element-491">
          {navGroups.map(group => {
          const isOpen = openGroups[group.title];
          const isActiveGroup = group.links.some(link => location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path));
          return <div key={group.title} className="mb-2" data-id="element-492">
                <button onClick={() => toggleGroup(group.title)} className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActiveGroup ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`} data-id="element-493">
                  <div className="flex items-center" data-id="element-494">
                    <group.icon className={`mr-3 h-5 w-5 ${isActiveGroup ? 'text-indigo-400' : 'text-slate-400'}`} data-id="element-495" />
                    {group.title}
                  </div>
                  {isOpen ? <ChevronDownIcon className="h-4 w-4 text-slate-400" data-id="element-496" /> : <ChevronRightIcon className="h-4 w-4 text-slate-400" data-id="element-497" />}
                </button>

                <AnimatePresence initial={false} data-id="element-498">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-499">
                      <div className="pl-11 pr-3 py-1 space-y-1" data-id="element-500">
                        {group.links.map(link => {
                    const isActive = location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path) && !link.path.includes('approval') && !location.pathname.includes('approval');
                    // Special exact match for approval routes to avoid highlighting multiple
                    const isExactActive = location.pathname === link.path || location.pathname.startsWith(`${link.path}/`);
                    return <NavLink key={link.path} to={link.path} className={({
                      isActive
                    }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors ${isExactActive ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`} data-id="element-501">
                              {link.label}
                            </NavLink>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </nav>
      </div>

      <div className="p-4 border-t border-slate-700 bg-slate-900" data-id="element-502">
        <div className="flex items-center" data-id="element-503">
          <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold" data-id="element-504">
            AD
          </div>
          <div className="ml-3" data-id="element-505">
            <p className="text-sm font-medium text-white" data-id="element-506">Admin User</p>
            <p className="text-xs text-slate-400" data-id="element-507">System Administrator</p>
          </div>
        </div>
      </div>
    </div>;
}